import css from './ActionPopup.module.css'
import Popup from "@/shared/components/layers/Popup/Popup"
import { ActionPopupProps } from "./ActionPopup.model"
import { Button } from '@/shared/components/common/Button/Button';
import CreatePlanPopup from '../CreatePlanPopup/CreatePlanPopup';
import CreateCompanysPlanPopup from '../CreateCompanysPlanPopup/CreateCompanysPlanPopup';
import CreateDepartmentForecastPopup from '../CreateDepartmentForecastPopup/CreateDepartmentForecastPopup';
import { useEffect, useRef, useState } from 'react';
import TableView from '@/shared/components/organism/TableView/TableView';
import useActionPopup from './useActionPopup';
import PlanSwitcher from '../../_common/planSwitcher/planSwitcher';
import EditPlanPopup from '../EditPlanPopup/EditPlanPopup';
import { useHeaderDashboard } from '../../../lib/useHeaderDashboard';
import EditCompanysPlanPopup from '../EditCompanysPlanPopup/EditCompanysPlanPopup';
import { usePlan } from '@/entities/Plan';
import { Select } from '@/shared/components/common/Select/Select';
import { useFilterPlans } from './useFilterPlans';
import { DatePicker } from '@/shared/components/common/DatePicker/DatePicker';
import PlanHistoryPopup, { PlanHistoryMode } from '@/features/Sale/PlanHistory';
import { canManageDepartment } from '@/features/Sale/PlanHistory/utils';
import { useAuth } from '@/features/Auth/lib/useAuth';
import { useWorkspace } from '@/features/Workspace/lib/useWorkspace';

function ActionPopup(props: ActionPopupProps) {
  const { open, onClose } = props;
  const [historyMode, setHistoryMode] = useState<PlanHistoryMode | null>(null);
  const actionButtons = useRef<HTMLDivElement>(null);
  const returnFocusTo = useRef<PlanHistoryMode | null>(null);
  const { user } = useAuth();
  const { departments } = useWorkspace();
  const canUseHistory = departments.some(d => canManageDepartment(user, d.id));
  useEffect(() => { if (!open) setHistoryMode(null); }, [open]);
  useEffect(() => {
    if (historyMode) { returnFocusTo.current = historyMode; return; }
    if (open && returnFocusTo.current) {
      actionButtons.current?.querySelector<HTMLButtonElement>(`[data-history-mode="${returnFocusTo.current}"]`)?.focus();
    }
    returnFocusTo.current = null;
  }, [historyMode, open]);
  const [createPlanPopupOpen, setCreatePlanPopupOpen] = useState(false);
  const [createCompanysPlanPopupOpen, setCreateCompanysPlanPopupOpen] = useState(false);
  const [createDepartmentForecastPopupOpen, setCreateDepartmentForecastPopupOpen] = useState(false);
  const { loading } = usePlan();
  const {filteredPlans, showOwnPlans, showAssignedPlans, showAllPlans, value, setValue} = useActionPopup();
  const {
    filters,
    setFilters,
    periodValue,
    setPeriodValue,
    periodPickerType,
    setPeriodPickerType,
    filterableKeys,
    filterOptions,
    filteredByFilters,
  } = useFilterPlans(filteredPlans);
  const { selectedPlan, setSelectedPlan, openEditPlanPopup, selectedCompanysPlan, setSelectedCompanysPlan, openEditCompanysPlanPopup } = useHeaderDashboard();

  return (
    <>
    <Popup open={open && !historyMode} onClose={onClose} title='План продаж' size='xl' fullHeight>
      <div className={css.heading}>
        <PlanSwitcher
          showOwnPlans={showOwnPlans}
          showAssignedPlans={showAssignedPlans}
          showAllPlans={showAllPlans}
          value={value!}
          onChange={setValue}
        />
        {/* <SubDepartmentSelect/> */}
      </div>
      {/* Фильтры */}
      <div style={{ display: 'flex', gap: 12, marginBottom: 12 }}>
        {/* DatePicker для фильтрации по периоду */}
        <div style={{ minWidth: 220, height: '100%', alignContent: 'end' }}>
          <DatePicker
            value={periodValue}
            onChange={setPeriodValue}
            pickerType={periodPickerType}
            onPickerTypeChange={setPeriodPickerType}
            placeholder="Период"
          />
        </div>
        {filterableKeys.map(key => (
          <Select
            key={key}
            label={key}
            options={filterOptions[key]}
            value={filters[key] || ''}
            onChange={val => setFilters(f => ({ ...f, [key]: val }))}
            className={''}
          />
        ))}
      </div>
      <p>
        Здесь показаны исходные назначения планов. Распределение после передачи и зафиксированные
        периоды смотрите через кнопки «Срез» и «Передача плана» внизу этого окна.
        После передачи основные планы отдела за этот месяц заблокированы для редактирования;
        после среза блокируется весь закрытый период.
      </p>
      <TableView data={filteredByFilters} loading={loading.myPlans || loading.planTypes}/>

      <div className={css.buttonWrapper} ref={actionButtons}>
        <Button onClick={() => setCreatePlanPopupOpen(true)}>
          Создать план продаж
        </Button>

        <Button onClick={() => setCreateCompanysPlanPopupOpen(true)}>
          Создать план по компаниям
        </Button>

        <Button onClick={() => setCreateDepartmentForecastPopupOpen(true)}>
          Создать прогноз отдела
        </Button>
        {canUseHistory && <>
          <Button data-history-mode="snapshot" variant="secondary" onClick={() => setHistoryMode('snapshot')}>Срез</Button>
          <Button data-history-mode="analytics" variant="secondary" onClick={() => setHistoryMode('analytics')}>Аналитика срезов</Button>
          <Button data-history-mode="transfer" variant="secondary" onClick={() => setHistoryMode('transfer')}>Передача плана</Button>
        </>}
      </div>
    </Popup>
    {open && historyMode && <PlanHistoryPopup mode={historyMode} onClose={() => setHistoryMode(null)}/>}

    <CreatePlanPopup open={createPlanPopupOpen} onClose={() => setCreatePlanPopupOpen(false)} />
    <CreateCompanysPlanPopup open={createCompanysPlanPopupOpen} onClose={() => setCreateCompanysPlanPopupOpen(false)} />
    <CreateDepartmentForecastPopup
      open={createDepartmentForecastPopupOpen}
      onClose={() => setCreateDepartmentForecastPopupOpen(false)}
    />
    <EditPlanPopup open={openEditPlanPopup} onClose={() => setSelectedPlan(null)} id={selectedPlan} />
    <EditCompanysPlanPopup open={openEditCompanysPlanPopup} onClose={() => setSelectedCompanysPlan(null)} id={selectedCompanysPlan} />
    </>
  )
}

export default ActionPopup
